import React from 'react';
/**
 * PracticeSession full-page states — Loading, Error, Empty variants.
 *
 * Extracted from PracticeSession.tsx in s69 to reduce its size from 978→~680 lines.
 * All components are pure presentational (no state management).
 */
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { AlertCircle, BookmarkCheck } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DjayAvatar, DjayToetsAvatar, getDjayThinkingMessage } from '../../mascot';
// ── Loading Spinner (shared animation) ───────────────────────
// F-A11Y-4: respect prefers-reduced-motion. The infinite scale/rotate loop
// is gated on useReducedMotion() so users with motion-sensitivity (or who
// have set the OS-level reduced-motion preference) see a still Djay rather
// than a continuously breathing one.
function LoadingSpinner() {
  const reduce = useReducedMotion();
  return <motion.div animate={reduce ? undefined : {
    scale: [1, 1.05, 1],
    rotate: [0, 3, -3, 0]
  }} transition={reduce ? undefined : {
    duration: 2.5,
    repeat: Infinity,
    ease: 'easeInOut'
  }} className="mx-auto mb-6" data-id="element-10123">
      {/* @djay-slot: practice-loading */}
      <DjayAvatar size="xl" emotion="thinking" data-id="element-10124" />
    </motion.div>;
}
// ── Loading State ────────────────────────────────────────────
export function SessionLoadingState() {
  return <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50" data-id="element-10125">
      <div className="text-center" data-id="element-10126">
        <LoadingSpinner data-id="element-10127" />
        <h3 className="text-2xl font-black text-slate-800 mb-3" data-id="element-10128">
          Even geduld...
        </h3>
        <p className="text-slate-500 font-medium text-lg" data-id="element-10129">
          {getDjayThinkingMessage().text}
        </p>
      </div>
    </div>;
}
// ── Error State (generation failed) ──────────────────────────
export function SessionErrorState({
  onExit
}: {
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-id="element-10130">
      <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-rose-100 border-2 border-rose-100" data-id="element-10131">
        <div className="text-6xl mb-6" data-id="element-10132">😕</div>
        <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-10133">
          Er ging iets mis
        </h2>
        <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-10134">
          De oefensessie kon niet worden geladen.
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-10135">
          Terug naar overzicht
        </Button>
      </div>
    </div>;
}
// ── Empty State (fallback) ───────────────────────────────────
export function SessionEmptyFallback({
  onExit
}: {
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-id="element-10136">
      <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-indigo-100 border-2 border-indigo-50" data-id="element-10137">
        <div className="text-6xl mb-6" data-id="element-10138">🎯</div>
        <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-10139">
          Geen vragen gevonden
        </h2>
        <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-10140">
          Er zijn momenteel geen vragen beschikbaar voor deze selectie.
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-10141">
          Terug naar overzicht
        </Button>
      </div>
    </div>;
}
// ── Empty State: No Content ──────────────────────────────────
export function SessionEmptyNoContent({
  onExit
}: {
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-id="element-10142">
      <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-amber-100 border-2 border-amber-50" data-id="element-10143">
        <div className="text-6xl mb-6" data-id="element-10144">📭</div>
        <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-10145">
          Geen oefenstof beschikbaar
        </h2>
        <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-10146">
          Er is nog geen oefenstof beschikbaar voor dit onderwerp. Probeer een
          ander onderwerp!
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-10147">
          Terug naar overzicht
        </Button>
      </div>
    </div>;
}
// ── Empty State: All Mastered ────────────────────────────────
export function SessionEmptyAllMastered({
  onExit
}: {
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-id="element-10148">
      <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-emerald-100 border-2 border-emerald-50" data-id="element-10149">
        <div className="text-6xl mb-6" data-id="element-10150">🏆</div>
        <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-10151">
          Alles onder de knie!
        </h2>
        <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-10152">
          Je hebt alle beschikbare vragen voor dit onderwerp al goed beantwoord.
          Geweldig gedaan!
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-emerald-200" data-id="element-10153">
          Terug naar overzicht
        </Button>
      </div>
    </div>;
}
// ── Safety Error (question undefined but session has questions) ─
export function SessionSafetyError({
  onExit
}: {
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-id="element-10154">
      <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-rose-100 border-2 border-rose-100" data-id="element-10155">
        <div className="text-6xl mb-6" data-id="element-10156">🤔</div>
        <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-10157">
          Er ging iets mis
        </h2>
        <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-10158">
          We konden de vraag niet laden. Probeer het nog eens!
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-10159">
          Terug naar overzicht
        </Button>
      </div>
    </div>;
}
// ── Shared modal scaffolding ─────────────────────────────────
// Both exit modals (practice + assessment) share an identical backdrop +
// spring-in card frame. Extracted to keep the modals themselves focused on
// copy / visuals / call-to-action.
function ExitModalFrame({
  show,
  onDismiss,
  borderTone,
  children
}: {
  show: boolean;
  onDismiss: () => void;
  borderTone: 'indigo' | 'amber';
  children: React.ReactNode;
}) {
  const borderClass = borderTone === 'indigo' ? 'border-indigo-100' : 'border-amber-100';
  return <AnimatePresence data-id="element-10160">
      {show && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50" onClick={onDismiss} aria-hidden="true" data-id="element-10161" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} transition={{
        type: 'spring',
        bounce: 0.2,
        duration: 0.4
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" data-id="element-10162">
            <Card className={`max-w-md w-full p-8 shadow-2xl border-2 ${borderClass} bg-white rounded-3xl`} data-id="element-10163">
              {children}
            </Card>
          </motion.div>
        </>}
    </AnimatePresence>;
}
// ── Practice Exit Modal ──────────────────────────────────────
// Used during a free-practice / lab / fluency session. Copy is intentionally
// reassuring: practice answers are persisted per-question via the onAnswer
// callback (mastery, XP, daily streak all update on submit), so stopping
// mid-session does NOT lose work — only the end-of-session summary screen.
// This modal is distinct from the assessment ExitConfirmModal below, which
// uses the stricter "incomplete attempt" framing required by 'toets' mode.
export function PracticeExitModal({
  show,
  answeredCount,
  totalQuestions,
  onContinue,
  onStop
}: {
  show: boolean;
  answeredCount: number;
  totalQuestions: number;
  onContinue: () => void;
  onStop: () => void;
}) {
  return <ExitModalFrame show={show} onDismiss={onContinue} borderTone="indigo" data-id="element-10164">
      <div className="text-center" data-id="element-10165">
        <div className="mx-auto mb-4 flex justify-center" data-id="element-10166">
          <DjayAvatar size="lg" emotion="thinking" animate data-id="element-10167" />
        </div>
        <h2 id="practice-exit-title" className="text-2xl font-black text-slate-900 mb-3 tracking-tight" data-id="element-10168">
          Stoppen met oefenen?
        </h2>
        <p className="text-slate-600 font-medium mb-6 leading-relaxed" data-id="element-10169">
          Geen probleem — je voortgang blijft bewaard. Volgende keer pak je
          gewoon weer op waar je gebleven was.
        </p>
        <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 mb-6 text-left" data-id="element-10170">
          <div className="flex items-start gap-3" data-id="element-10171">
            <BookmarkCheck className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" data-id="element-10172" />
            <div className="text-sm text-indigo-900 font-medium leading-relaxed" data-id="element-10173">
              <p className="font-bold mb-0.5" data-id="element-10174">
                Je antwoorden zijn al opgeslagen
              </p>
              <p className="text-indigo-800" data-id="element-10175">
                {answeredCount} van de {totalQuestions} vragen tellen mee voor
                je mastery, XP en streak.
              </p>
            </div>
          </div>
        </div>
        <div className="flex gap-3" data-id="element-10176">
          <Button onClick={onContinue} variant="primary" size="lg" fullWidth data-id="element-10177">
            Doorgaan met oefenen
          </Button>
          <Button onClick={onStop} variant="secondary" size="lg" fullWidth className="text-slate-700" data-id="element-10178">
            Stop voor nu
          </Button>
        </div>
      </div>
    </ExitModalFrame>;
}
// ── Exit Confirmation Modal (assessment / toets mode) ────────
// Stricter framing for graded 'toets' sessions where stopping mid-session
// produces an explicit "incomplete" attempt record. Visual now uses
// DjayToetsAvatar (test-themed Djay) instead of the generic 🤔 emoji so the
// modal matches the rest of the assessment shell's visual language.
export function ExitConfirmModal({
  show,
  currentIndex,
  totalQuestions,
  onContinue,
  onStop
}: {
  show: boolean;
  currentIndex: number;
  totalQuestions: number;
  onContinue: () => void;
  onStop: () => void;
}) {
  return <ExitModalFrame show={show} onDismiss={onContinue} borderTone="amber" data-id="element-10179">
      <div className="text-center" data-id="element-10180">
        <div className="mx-auto mb-4 flex justify-center" data-id="element-10181">
          <DjayToetsAvatar domain="toets" size="lg" animate data-id="element-10182" />
        </div>
        <h2 className="text-2xl font-black text-slate-900 mb-3 tracking-tight" data-id="element-10183">
          Toets stoppen?
        </h2>
        <p className="text-slate-600 font-medium mb-6 leading-relaxed" data-id="element-10184">
          Weet je zeker dat je wilt stoppen? Je voortgang wordt opgeslagen als
          onvolledig.
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left" data-id="element-10185">
          <div className="flex items-start gap-3" data-id="element-10186">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-10187" />
            <p className="text-sm text-amber-800 font-medium" data-id="element-10188">
              Je hebt {currentIndex + 1} van de {totalQuestions} vragen
              beantwoord. Als je stopt, kun je later opnieuw beginnen.
            </p>
          </div>
        </div>
        <div className="flex gap-3" data-id="element-10189">
          <Button onClick={onContinue} variant="primary" size="lg" fullWidth data-id="element-10190">
            Doorgaan met toets
          </Button>
          <Button onClick={onStop} variant="secondary" size="lg" fullWidth className="text-rose-600 border-rose-200 hover:bg-rose-50" data-id="element-10191">
            Stoppen
          </Button>
        </div>
      </div>
    </ExitModalFrame>;
}